<template>
  <div class="content-wrapper">
    <div class="platform-header">
      <div class="title-box">
        <span>平台给我开票</span>
      </div>
      <div class="platform-info">
        <div class="info-left">
          <span class="info-text">你的当前发票抬头是：{{ platformTitle }}</span>
          <span class="info-sub">主体抬头信息同步店铺入驻资质，无法直接修改，需前往店铺资质中心变更审核</span>
        </div>
        <span class="info-link" @click="titleModalVisible = true">管理信息</span>
      </div>
      <div class="divider-row"></div>
      <div class="filter-box">
        <div class="left">
          <div class="filter-item-box">
            <span>状态</span>
            <div class="filter-item-value-box">
              <div
                class="filter-item"
                :class="{ active: form.status === 'all' }"
                @click="platformFilter('status', 'all')"
              >
                <span>全部</span>
                <span>{{ count.all }}</span>
              </div>
              <div
                class="filter-item"
                :class="{ active: form.status === 'pending' }"
                @click="platformFilter('status', 'pending')"
              >
                <span>待开票</span>
                <span>{{ count.pending }}</span>
              </div>
              <div
                class="filter-item"
                :class="{ active: form.status === 'applying' }"
                @click="platformFilter('status', 'applying')"
              >
                <span>申请中</span>
                <span>{{ count.applying }}</span>
              </div>
              <div
                class="filter-item"
                :class="{ active: form.status === 'invoiced' }"
                @click="platformFilter('status', 'invoiced')"
              >
                <span>已开票</span>
                <span>{{ count.invoiced }}</span>
              </div>
              <div
                class="filter-item"
                :class="{ active: form.status === 'rejected' }"
                @click="platformFilter('status', 'rejected')"
              >
                <span>已驳回</span>
                <span>{{ count.rejected }}</span>
              </div>
            </div>
          </div>
        </div>
        <div class="right">
          <div
            class="batch-btn outline"
            :class="{ disabled: !checkedList.length }"
            @click="checkedList.length && onBatchInvoice()"
          >
            批量开票
          </div>
          <div class="batch-btn outline" @click="onExport">导出数据</div>
        </div>
      </div>
    </div>
    <div class="table-box">
      <table>
        <colgroup>
          <col style="width: 50px" />
          <col style="width: 180px" />
          <col style="width: 120px" />
          <col style="width: 100px" />
          <col style="width: 100px" />
          <col v-if="showInvoiceType" style="width: 120px" />
          <col style="width: 80px" />
          <col style="width: 140px" />
        </colgroup>
        <thead>
          <tr>
            <th>
              <div class="col-check">
                <Checkbox
                  :indeterminate="checkIndeterminate"
                  :value="checkAll"
                  @on-change="onCheckAll"
                ></Checkbox>
              </div>
            </th>
            <th>订单编号</th>
            <th>收益类型</th>
            <th>账期</th>
            <th>金额(含税)</th>
            <th v-if="showInvoiceType">发票类型</th>
            <th>状态</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in list" :key="item.id">
            <td>
              <div class="col-check">
                <Checkbox
                  :value="checkedList.includes(item.id)"
                  :label="item.id"
                  @on-change="(val) => onSingleCheck(val, item.id)"
                ></Checkbox>
              </div>
            </td>
            <td>
              <div class="col-text">{{ item.order_no }}</div>
            </td>
            <td>
              <div class="col-text">{{ typeText(item.type) }}</div>
            </td>
            <td>
              <div class="col-text">{{ item.period }}</div>
            </td>
            <td>
              <div class="col-text">¥{{ item.amount }}</div>
            </td>
            <td v-if="showInvoiceType">
              <div class="col-text">{{ taxTypeText(item.tax_type) }}</div>
            </td>
            <td>
              <div class="col-status">
                <span :class="getStatusClass(item.status)">{{ getStatusText(item.status) }}</span>
              </div>
            </td>
            <td>
              <div class="col-action">
                <template v-if="item.status === 0">
                  <span class="action-link" @click="onApply(item)">申请开票</span>
                </template>
                <template v-if="item.status === 1">
                  <span class="action-link" @click="showDetail(item)">查看详情</span>
                </template>
                <template v-if="item.status === 2">
                  <span class="action-link" @click="showDetail(item)">查看发票</span>
                </template>
                <template v-if="item.status === 4">
                  <span class="action-link gray" @click="showDetail(item)">查看原因</span>
                  <span class="action-link" @click="onCancelApply(item)">撤销申请</span>
                </template>
              </div>
            </td>
          </tr>
          <tr v-if="!list.length">
            <td :colspan="showInvoiceType ? 8 : 7" class="empty-tip">暂无数据</td>
          </tr>
        </tbody>
      </table>
    </div>
    <div class="page-container">
      <Page
        :total="total"
        :current="form.page"
        :page-size="form.limit"
        size="small"
        prev-text="上一页"
        next-text="下一页"
        @on-change="onPageChange"
      ></Page>
    </div>

    <!-- 申请开票弹窗 -->
    <ApplyInvoiceModal
      :visible.sync="applyModalVisible"
      :is-batch="applyModalIsBatch"
      :title-type="titleData.type"
      :title-data="titleData"
      :items="applyModalItems"
      @edit-title="titleModalVisible = true"
      @confirm="onApplyConfirm"
    />

    <!-- 申请开票详情弹窗 -->
    <InvoiceApplyDetailModal
      :visible.sync="applyDetailModalVisible"
      :data="applyDetailData"
      @cancel-apply="onCancelApplyDetail"
      @re-apply="onReApply"
      @download="onDownloadInvoice"
    />

    <!-- 我的发票抬头 -->
    <InvoiceTitleModal
      :visible.sync="titleModalVisible"
      :title-data="titleData"
      @confirm="onTitleSave"
      @cancel="titleModalVisible = false"
    />
  </div>
</template>

<script>
import {
  InvoicePlatformList,
  InvoicePlatformApply,
  InvoicePlatformCancel,
  InvoicePlatformDownload,
  InvoiceTitle,
  InvoiceTitleSave,
} from '@/api/index'
import util from '@/utils/util'
import ApplyInvoiceModal from './ApplyInvoiceModal.vue'
import InvoiceApplyDetailModal from './InvoiceApplyDetailModal.vue'
import InvoiceTitleModal from './InvoiceTitleModal.vue'

export default {
  name: 'PlatformInvoiceTab',
  components: {
    ApplyInvoiceModal,
    InvoiceApplyDetailModal,
    InvoiceTitleModal,
  },
  data() {
    return {
      // 查询表单
      form: {
        page: 1,
        limit: 10,
        status: 'all',
      },
      total: 0,
      count: {
        all: 0,
        pending: 0,
        applying: 0,
        invoiced: 0,
        rejected: 0,
      },
      platformTitle: '',
      list: [],
      loading: false,
      checkedList: [],
      checkAll: false,
      checkIndeterminate: false,

      // 发票抬头信息（字段对齐 user_tax_info 表，type: 0-个人, 1-企业）
      titleData: {
        type: 0,
        title: '',
        tax_number: '',
        company_address: '',
        phone: '',
        bank_name: '',
        bank_account: '',
        send_mail: '',
        tax_type: 0,
        is_default: 0,
      },

      // 弹窗
      applyModalVisible: false,
      applyModalIsBatch: false,
      applyModalItems: [],

      applyDetailModalVisible: false,
      applyDetailData: {},

      titleModalVisible: false,
    }
  },
  computed: {
    showInvoiceType() {
      return this.form.status === 'invoiced'
    },
  },
  created() {
    this.fetchTitle()
    this.fetchList()
  },
  methods: {
    // ===== 状态映射 =====
    getStatusClass(status) {
      const map = {
        0: 'warn',
        1: 'info',
        2: 'success',
        4: 'danger',
      }
      return map[status] || ''
    },
    getStatusText(status) {
      const map = {
        0: '待开票',
        1: '申请中',
        2: '已开票',
        4: '已驳回',
      }
      return map[status] || status
    },
    feeTypeText(feeType) {
      return feeType == 1 ? '佣金扣费' : '其他'
    },
    typeText(type) {
      return type == 1 ? '商城订单结算' : '其他'
    },
    formatTaxRate(rate) {
      const r = parseFloat(rate || 0)
      if (!r) return '0%'
      const pct = (Math.round(r * 10000) / 100).toString()
      return pct + '%'
    },
    taxTypeText(taxType) {
      return taxType == 1 ? '电子专用发票' : '电子普通发票'
    },

    // ===== 抬头 =====
    async fetchTitle() {
      try {
        const res = await InvoiceTitle()
        const data = res.data || {}
        if (data && data.title !== undefined) {
          this.titleData = Object.assign({}, this.titleData, data)
          this.platformTitle = data.title || ''
        }
      } catch (e) {
        // 使用默认空抬头
      }
    },

    // ===== 列表加载 =====
    async fetchList() {
      this.loading = true
      try {
        const res = await InvoicePlatformList(this.form)
        const data = res.data || {}
        this.list = data.list || []
        this.total = data.total || 0
        this.count = Object.assign(
          { all: 0, pending: 0, applying: 0, invoiced: 0, rejected: 0 },
          data.count || {}
        )
        this.checkedList = []
        this.onCheckChange()
      } catch (e) {
        this.$Message.error((e && e.msg) || '获取列表失败')
      } finally {
        this.loading = false
      }
    },
    platformFilter(type, value) {
      this.form[type] = value
      this.form.page = 1
      this.fetchList()
    },
    onPageChange(page) {
      this.form.page = page
      this.fetchList()
    },

    // ===== 勾选 =====
    onSingleCheck(checked, id) {
      if (checked) {
        if (!this.checkedList.includes(id)) {
          this.checkedList.push(id)
        }
      } else {
        const index = this.checkedList.indexOf(id)
        if (index > -1) {
          this.checkedList.splice(index, 1)
        }
      }
      this.onCheckChange()
    },
    onCheckChange() {
      if (this.checkedList.length === 0) {
        this.checkIndeterminate = false
        this.checkAll = false
      } else if (this.checkedList.length === this.list.length) {
        this.checkIndeterminate = false
        this.checkAll = true
      } else {
        this.checkIndeterminate = true
        this.checkAll = false
      }
    },
    onCheckAll() {
      if (this.checkIndeterminate) {
        this.checkAll = false
      } else {
        this.checkAll = !this.checkAll
      }
      this.checkIndeterminate = false
      if (this.checkAll) {
        this.checkedList = this.list.map((item) => item.id)
      } else {
        this.checkedList = []
      }
    },

    // ===== 操作 =====
    // 导出平台给我开票数据：选中则按选中导出，不选中则按当前筛选条件（status）导出
    async onExport() {
      const params = {
        status: this.form.status,
      }
      if (this.checkedList.length) {
        params.ids = this.checkedList
      }
      try {
        await util.downloadExcel(
          '/shop/invoice/platform-export',
          params,
          this.$store.state.token,
          '平台给我开票'
        )
        this.$Message.success('导出成功')
      } catch (e) {
        this.$Message.error((e && e.message) || '导出失败')
      }
    },

    // 申请开票前置校验：未登记发票抬头时引导先到“管理信息”新增
    checkTitle() {
      if (!this.titleData.id) {
        this.$Message.warning('请先更新发票抬头')
        return false
      }
      return true
    },

    // 单条申请开票
    onApply(item) {
      if (!this.checkTitle()) {
        return
      }
      this.applyModalIsBatch = false
      this.applyModalItems = [
        {
          id: item.id,
          order_no: item.order_no,
          type: item.type,
          period: item.period,
          amount: item.amount,
        },
      ]
      this.applyModalVisible = true
    },

    // 批量申请开票
    onBatchInvoice() {
      if (!this.checkTitle()) {
        return
      }
      const selectedItems = this.list.filter((item) => this.checkedList.includes(item.id))
      if (!selectedItems.length) {
        this.$Message.warning('请先选择要开票的记录')
        return
      }
      this.applyModalIsBatch = true
      this.applyModalItems = selectedItems.map((item) => ({
        id: item.id,
        order_no: item.order_no,
        type: item.type,
        period: item.period,
        amount: item.amount,
      }))
      this.applyModalVisible = true
    },

    // 申请开票确认
    async onApplyConfirm(data) {
      try {
        const items = (data.items || []).map((i) => ({ id: i.id }))
        await InvoicePlatformApply({
          title_id: this.titleData.id || 0,
          tax_type: data.invoice_type == 'special' ? 1 : 0,
          send_mail: data.email,
          source: 3,
          items,
        })
        this.$Message.success(this.applyModalIsBatch ? '批量申请提交成功' : '申请提交成功')
        this.applyModalVisible = false
        this.fetchList()
      } catch (e) {
        this.$Message.error((e && e.msg) || '申请失败')
      }
    },

    // 查看详情（申请中/已驳回/已开票）
    showDetail(item) {
      this.applyDetailData = { ...item }
      this.applyDetailModalVisible = true
    },

    // 撤销申请（从详情弹窗）
    async onCancelApplyDetail(data) {
      try {
        await InvoicePlatformCancel({ id: data.id })
        this.$Message.success('撤销申请成功')
        this.applyDetailModalVisible = false
        this.fetchList()
      } catch (e) {
        this.$Message.error((e && e.msg) || '撤销失败')
      }
    },

    // 重新申请（从详情弹窗）
    onReApply(data) {
      this.applyModalIsBatch = false
      this.applyModalItems = data.detail_list
        ? data.detail_list.map((d) => ({
            id: data.id,
            order_no: d.order_no,
            fee_type: d.fee_type,
            period: d.period,
            tax_rate: d.tax_rate,
            amount: d.amount,
          }))
        : []
      this.applyModalVisible = true
    },

    // 下载电子发票
    async onDownloadInvoice(data) {
      try {
        const res = await InvoicePlatformDownload({ id: data.id })
        const url = (res.data && res.data.url) || ''
        if (url) {
          window.open(url)
        } else {
          this.$Message.warning('暂无发票文件')
        }
      } catch (e) {
        this.$Message.error((e && e.msg) || '下载失败')
      }
    },

    // 列表上直接撤销申请
    onCancelApply(item) {
      this.$Modal.confirm({
        title: '确认撤销',
        content: '确定撤销该开票申请吗？',
        onOk: async () => {
          try {
            await InvoicePlatformCancel({ id: item.id })
            this.$Message.success('撤销成功')
            this.fetchList()
          } catch (e) {
            this.$Message.error((e && e.msg) || '撤销失败')
          }
        },
      })
    },

    // 保存抬头
    async onTitleSave(data) {
      try {
        await InvoiceTitleSave({ ...data })
        this.$Message.success('保存成功')
        this.titleModalVisible = false
        this.fetchTitle()
      } catch (e) {
        this.$Message.error((e && e.msg) || '保存失败')
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import './invoice-common.scss';

.empty-tip {
  text-align: center;
  color: #999999;
  padding: 40px 0;
}

.platform-header {
  .platform-info {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-top: 16px;
    padding: 16px;
    background: #f7faff;
    border-radius: 4px;

    .info-left {
      display: flex;
      flex-direction: column;
      gap: 4px;

      .info-text {
        font-size: 14px;
        color: #1a1a1a;
      }

      .info-sub {
        font-size: 12px;
        color: #999999;
      }
    }

    .info-link {
      font-size: 14px;
      color: #0d99ff;
      cursor: pointer;
      flex-shrink: 0;

      &:hover {
        opacity: 0.8;
      }
    }
  }
}
</style>
